import {ReceiptRepo} from "../api/receipt.repo";
import {inject, Injectable} from "@angular/core";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {forkJoin, Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText, sqlYesNo} from "./duckdb.helper";
import {SearchReceiptRes} from "../dto/search/search-receipt.res";
import {LoggingService} from "../../services/logger/logging.service";
import {map} from "rxjs/operators";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {Additionals} from "../item/additionals";
import {ReceiptDto} from "../dto/receipt.dto";
import {CheckDto} from "../dto/check.dto";
import {SearchBookingCategoryRes} from "../dto/search/search-booking-category.res";
import {I18nService} from "../../services/i18n/i18n.service";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";

@Injectable({providedIn: 'root'})
export class ReceiptDuckDb extends ReceiptRepo {

  private readonly duck = inject(DuckDbHelper);
  private readonly log = inject(LoggingService);
  private readonly i18n = inject(I18nService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override hasDuplicates(_org: OrgDto, _receiptId: string, _receiptNumber: string, _contactId: string): Observable<WrapperDto<CheckDto>> {
    return DuckDbHelper.dataObs('CheckDto', new CheckDto({result: false}));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override getById(_org: OrgDto, receiptId: string, _add?: Additionals): Observable<WrapperDto<ReceiptDto>> {
    const selectReceipt = `
      SELECT r.id                                                    AS id,
             r.type                                                  AS type,
             r.number                                                AS number,
             r.receipt_number                                        AS receiptNumber,
             r.receipt_date                                          AS receiptDate,
             r.total_net_amount                                      AS totalNetAmount,
             r.total_gross_amount                                    AS totalGrossAmount,
             r.total_vat_amount                                      AS totalVatAmount,
             r.currency_code                                         AS currencyCode,
             r.media_id                                              AS mediaId,
             (CASE WHEN r.reviewed IS NULL THEN FALSE ELSE TRUE END) AS reviewed,
             (CASE WHEN r.booked IS NULL THEN FALSE ELSE TRUE END)   AS booked,
             c.contact_id                                            AS contactId,
             r.tech_type                                             AS techType,
             r.tech_media_id                                         AS techMediaId,
             r.destination_city                                      AS destinationCity,
             r.destination_zip_code                                  AS destinationZipCode,
             r.destination_contact_id                                AS destinationContactId,
             r.destination_distance                                  AS destinationDistance,
             r.booking_category_id                                   AS bookingCategoryId,
             r.booking_category_enum                                 AS bookingCategory
      FROM receipt r
             LEFT JOIN receipt_contact c ON c.id = r.contact_id
      WHERE r.id = ${sqlText(receiptId)}`;

    const selectPosition = `
      SELECT p.id                      AS id,
             p.pos                     AS pos,
             p.number                  AS number,
             p.quantity                AS quantity,
             p.description             AS description,
             p.net_amount              AS netAmount,
             p.gross_amount            AS grossAmount,
             p.vat_amount              AS vatAmount,
             p.total_net_amount        AS totalNetAmount,
             p.total_gross_amount      AS totalGrossAmount,
             p.total_vat_amount        AS totalVatAmount,
             p.vat_rate                AS vatRate,
             p.vat_rate_value          AS vatRateValue,
             p.old_part                AS oldPart,
             p.old_part_vat_amount     AS oldPartTotalVatAmount,
             p.old_part_list_price     AS oldPartListPrice,
             p.price_mileage_allowance AS priceMileageAllowance,
             p.booking_category_id     AS bookingCategoryId,
             p.booking_category_enum   AS bookingCategory
      FROM receipt_position p
             LEFT JOIN receipt r ON p.receipt_id = r.id
      WHERE r.id = ${sqlText(receiptId)}`;

    const selectPositionDiscount = `
      SELECT d.id                 AS id,
             d.type               AS type,
             d.value              AS "value",
             d.net_amount         AS netAmount,
             d.gross_amount       AS grossAmount,
             d.vat_amount         AS vatAmount,
             d.total_net_amount   AS totalNetAmount,
             d.total_gross_amount AS totalGrossAmount,
             d.total_vat_amount   AS totalVatAmount,
             d.receipt_id         AS receipt_id,
             d.position_id        AS position_id
      FROM receipt r
             LEFT JOIN receipt_position p ON p.receipt_id = r.id
             LEFT JOIN receipt_discount d ON d.position_id = p.id
      WHERE r.id = ${sqlText(receiptId)}
        AND d.id IS NOT NULL;`;

    const selectContact = `
      SELECT c.id               AS id,
             c.type             AS type,
             c.manual_input     AS manualInput,
             c.contact_type     AS contactType,
             c.number           AS number,
             c.full_name        AS fullName,
             c.street           AS street,
             c.house_number     AS houseNumber,
             c.address_suffix   AS addressSuffix,
             c.city             AS city,
             c.zip_code         AS zipCode,
             c.post_office_box  AS postOfficeBox,
             c.country_alpha2   AS countryAlpha2,
             c.contact_id       AS contactId,
             c.vehicle_id       AS vehicleId,
             c.vehicle_odometer AS vehicleOdometer,
             c.vehicle_history  AS vehicleHistory,
             FALSE              AS completeAsLetter
      FROM main.receipt_contact c
             LEFT JOIN receipt r ON r.contact_id = c.id
      WHERE r.id = ${sqlText(receiptId)}`;

    const selectPayment = `
      SELECT p.id        AS id,
             p.completed AS completed,
             p.balance   AS balance,
             p.amount    AS amount,
      FROM payment p
             LEFT JOIN receipt r ON r.payment_id = p.id
      WHERE r.id = ${sqlText(receiptId)}
        AND p.belong = 'RECEIPT'`;

    const selectPaymentEntry = `
      SELECT e.id              AS id,
             e.amount          AS amount,
             e.method          AS method,
             e.date,
             e.bank_address_id AS bankAddressId,
             FALSE             AS editable,
             e.payment_id
      FROM payment p
             LEFT JOIN receipt r ON r.payment_id = p.id
             LEFT JOIN payment_entry e ON e.payment_id = p.id
      WHERE r.id = ${sqlText(receiptId)}
        AND p.belong = 'RECEIPT'`;

    return forkJoin([
      this.duck.sql(selectReceipt),
      this.duck.sql(selectPosition),
      this.duck.sql(selectContact),
      this.duck.sql(selectPayment),
      this.duck.sql(selectPaymentEntry),
      this.duck.sql(selectPositionDiscount)
    ]).pipe(map(([receiptRes, posRes, contactRes, paymentRes, entryRes, posDisRes]) => {
      if (DuckDbHelper.hasRes(receiptRes)) {
        if (DuckDbHelper.hasRes(posRes)) {
          if (DuckDbHelper.hasRes(posDisRes)) {
            posRes.forEach((pos: any) => pos.discounts = posDisRes.filter((d: any) => d.position_id === pos.id));
          }
          receiptRes[0].positions = posRes;
        }
        if (DuckDbHelper.hasRes(contactRes)) {
          receiptRes[0].contact = DuckDbHelper.firstRes(contactRes);
        }
        const payment = DuckDbHelper.firstRes(paymentRes);
        if (isDefined(payment)) {
          payment.entries = entryRes;
          receiptRes[0].payment = payment;
        }

        const receipt = new ReceiptDto(receiptRes[0]);
        return DuckDbHelper.data('ReceiptDto', receipt);
      }
      return DuckDbHelper.data('ReceiptDto', new ReceiptDto());
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchReceiptRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT r.id                                                    AS id,
             r.type                                                  AS type,
             r.number                                                AS number,
             r.receipt_number                                        AS receiptNumber,
             r.receipt_date                                          AS receiptDate,
             r.total_net_amount                                      AS totalNetAmount,
             r.total_gross_amount                                    AS totalGrossAmount,
             r.currency_code                                         AS currencyCode,
             r.media_id                                              AS mediaId,
             (CASE WHEN r.reviewed IS NULL THEN FALSE ELSE TRUE END) AS reviewed,
             r.booked                                                AS booked,
             p.completed                                             AS paymentCompleted,
             p.balance                                               AS paymentBalance,
             c.id                                                    AS dcId,
             c.type                                                  AS dcType,
             c.contact_type                                          AS dcContactType,
             c.number                                                AS dcContactNumber,
             c.full_name                                             AS dcContactFullName,
             d.id                                                    AS destinationContactId,
             d.contact_type                                          AS destinationContactType,
             d.number                                                AS destinationContactNumber,
             d.full_name                                             AS destinationContactFullName,
             COUNT(*)                                                   OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM receipt r
             LEFT JOIN payment p ON p.id = r.payment_id AND p.belong = 'RECEIPT'
             LEFT JOIN receipt_contact c ON c.id = r.contact_id
             LEFT JOIN receipt_contact d ON d.id = r.destination_contact_id
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const hasRes = DuckDbHelper.hasRes(res);
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchReceiptRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchReceiptRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      case 'receiptDate':
        field = 'r.receipt_date';
        break;
      case 'type':
        field = 'r.type';
        break;
      case 'number':
        field = 'r.number';
        break;
      case 'receiptNumber':
        field = 'r.receipt_number';
        break;
      case 'totalNetAmount':
        field = 'r.total_net_amount';
        break;
      case 'dcContactNumber':
        field = 'c.number';
        break;
      case 'totalGrossAmount':
        field = 'r.total_gross_amount';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 'r.receipt_date';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(r.number ILIKE ${sqlLike(request.query)}
          OR r.receipt_number ILIKE ${sqlLike(request.query)}
          OR c.number ILIKE ${sqlLike(request.query)}
          OR c.full_name ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    // Die Zahlung legt core erst beim Pruefen an: Ein ungepruefter Beleg hat keine und zaehlt nicht als unbezahlt.
    const filters: string[] = [
      sqlYesNo(request, 'reviewed', 'r.reviewed IS NOT NULL', 'r.reviewed IS NULL'),
      sqlYesNo(request, 'payment', 'p.completed = TRUE', 'p.completed = FALSE')
    ].filter((f) => isDefined(f));
    // noinspection DuplicatedCode see InvoiceDuckDb
    const dcContactIdFilter = request.filters.find((f) => f.id === 'dcContactId');
    if (isDefined(dcContactIdFilter) && isDefined(dcContactIdFilter.selected) && dcContactIdFilter.selected?.length > 0) {
      filters.push(`c.contact_id = ${sqlText(dcContactIdFilter.selected[0])}`);
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }


  public override bookingCategorySearch(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchBookingCategoryRes>> {
    const search = `SELECT id,
                           name,
                           system,
                           COUNT(*) OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING) AS total_results
                    FROM category`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const hasRes = DuckDbHelper.hasRes(res);
      if (hasRes) {
        res.filter((r: any) => r.name.startsWith('booking.category.'))
          .map((r: any) => r.name = this.i18n.translate(r.name));
      }
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchBookingCategoryRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: []
      });
      return DuckDbHelper.data('SearchBookingCategoryRes', result);
    }));
  }
}
